import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  MessageSquare,
  User,
  Clock,
  CheckCircle,
  Download,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotService } from '../../../services/chatbotService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const Conversations = () => {
  const { t } = useTranslation();
  const { formatDateTime, formatTime } = useDateFormat();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedChatbotId, setSelectedChatbotId] = useState('');

  const [conversations, setConversations] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  // Conversation detail modal
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const chatbotId = selectedChatbotId || null;
      const [convData, chatbotsData, statsData] = await Promise.all([
        chatbotService.getConversations(chatbotId, page, PAGE_SIZE),
        chatbots.length === 0 ? chatbotService.getChatbots() : Promise.resolve(null),
        chatbotService.getDashboardStats(),
      ]);

      const content = convData?.content || convData || [];
      setConversations(content);
      setTotalPages(convData?.totalPages || 1);
      setTotalElements(convData?.totalElements || content.length);

      if (chatbotsData !== null) setChatbots(chatbotsData || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [page, selectedChatbotId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewConversation = async (conv) => {
    setSelectedConversation(conv);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const data = await chatbotService.getConversationMessages(conv.id);
      setMessages(data || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return { color: 'bg-blue-100 text-blue-800', icon: MessageSquare, label: t('chatbot.conv_active') };
    }
    return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('chatbot.conv_ended') };
  };

  // Client-side search filter (the API handles chatbot filter & pagination)
  const filteredConversations = conversations.filter((conv) => {
    const name = conv.visitorName || conv.visitorEmail || '';
    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(conv.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (conv.chatbotName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && conv.isActive) ||
      (filterStatus === 'ended' && !conv.isActive);
    return matchesSearch && matchesFilter;
  });

  const activeCount = conversations.filter((c) => c.isActive).length;

  const exportConversations = () => {
    const escape = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['ID', 'Visitor Name', 'Visitor Email', 'Bot', 'Status', 'Messages', 'Started At'];
    const rows = filteredConversations.map((conv) => [
      conv.id,
      conv.visitorName || 'Anonymous',
      conv.visitorEmail || '',
      conv.chatbotName || '',
      conv.isActive ? 'Active' : 'Ended',
      conv.messageCount ?? 0,
      conv.createdAt ? formatDateTime(conv.createdAt) : '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversations_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 text-primary-600 hover:underline"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('chatbot.conversations')}</h1>
          <p className="text-gray-600 mt-1">{t('chatbot.conv_subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg border border-gray-200"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button className="btn btn-outline" onClick={exportConversations}>
            <Download className="w-5 h-5 mr-2" />
            {t('chatbot.conv_exportData')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {(stats?.totalConversations ?? totalElements).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">{t('chatbot.conv_totalConversations')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-blue-600">{activeCount}</div>
          <div className="text-sm text-gray-600">{t('chatbot.conv_activeNow')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-purple-600">
            {(stats?.totalMessages ?? 0).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">{t('chatbot.conv_totalMessages')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">
            {stats?.totalChatbots ?? chatbots.length}
          </div>
          <div className="text-sm text-gray-600">{t('chatbot.conv_chatbots')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('chatbot.conv_searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">{t('chatbot.conv_allStatus')}</option>
            <option value="active">{t('chatbot.conv_active')}</option>
            <option value="ended">{t('chatbot.conv_ended')}</option>
          </select>

          <select
            value={selectedChatbotId}
            onChange={(e) => { setSelectedChatbotId(e.target.value); setPage(0); }}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">{t('chatbot.conv_allBots')}</option>
            {chatbots.map((bot) => (
              <option key={bot.id} value={bot.id}>{bot.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filteredConversations.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('chatbot.conv_noConversations')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.conv_visitor')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.conv_bot')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.conv_messages')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('chatbot.conv_started')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredConversations.map((conv) => {
                const statusBadge = getStatusBadge(conv.isActive);
                const StatusIcon = statusBadge.icon;
                return (
                  <tr key={conv.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold text-gray-600 uppercase">
                          {conv.visitorName ? conv.visitorName.charAt(0) : <User className="w-4 h-4 text-gray-500" />}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {conv.visitorName || t('chatbot.conv_anonymous')}
                          </div>
                          {conv.visitorEmail && (
                            <div className="text-xs text-gray-500">{conv.visitorEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {conv.chatbotName && (
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white uppercase flex-shrink-0"
                            style={{ backgroundColor: conv.chatbotColor || '#6366f1' }}
                          >
                            {conv.chatbotName.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm text-gray-600">{conv.chatbotName || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {conv.messageCount ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {conv.createdAt ? formatDateTime(conv.createdAt) : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewConversation(conv)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title={t('chatbot.conv_viewMessages')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalElements > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
          {/* Info */}
          <p className="text-sm text-gray-500">
            {t('chatbot.conv_showing', {
              from: (page * PAGE_SIZE + 1).toLocaleString(),
              to:   Math.min((page + 1) * PAGE_SIZE, totalElements).toLocaleString(),
              total: totalElements.toLocaleString(),
            })}
          </p>

          {/* Page controls */}
          <div className="flex items-center gap-1">
            {/* Prev */}
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page numbers */}
            {(() => {
              const pages = [];
              const WINDOW = 2; // pages around current
              for (let i = 0; i < totalPages; i++) {
                const near = i === 0 || i === totalPages - 1 || Math.abs(i - page) <= WINDOW;
                if (!near) {
                  if (pages[pages.length - 1] !== '…') pages.push('…');
                  continue;
                }
                pages.push(i);
              }
              return pages.map((p_, idx) =>
                p_ === '…' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm select-none">…</span>
                ) : (
                  <button
                    key={p_}
                    onClick={() => setPage(p_)}
                    className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium border transition-colors ${
                      p_ === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {p_ + 1}
                  </button>
                )
              );
            })()}

            {/* Next */}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {selectedConversation.chatbotName && (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white uppercase flex-shrink-0"
                    style={{ backgroundColor: selectedConversation.chatbotColor || '#6366f1' }}
                  >
                    {selectedConversation.chatbotName.charAt(0)}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedConversation.visitorName || t('chatbot.conv_anonymous')}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedConversation.chatbotName} · {selectedConversation.messageCount ?? 0} messages ·{' '}
                    {selectedConversation.createdAt
                      ? formatDateTime(selectedConversation.createdAt)
                      : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>{t('chatbot.conv_noMessages')}</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const role    = (msg.role || '').toLowerCase();
                  const isUser  = role === 'user';
                  const isAgent = role === 'agent';

                  const senderLabel = isUser
                    ? (selectedConversation.visitorName || t('chatbot.conv_visitor'))
                    : isAgent
                    ? (msg.agentName || t('chatbot.conv_agent'))
                    : (selectedConversation.chatbotName || t('chatbot.conv_bot'));

                  const bubbleClass = isUser
                    ? 'bg-gray-100 text-gray-800'
                    : isAgent
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-50 text-green-700 italic';

                  return (
                    <div key={msg.id ?? idx} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${bubbleClass}`}>
                        <p className="text-xs font-semibold mb-1 opacity-60">{senderLabel}</p>
                        {msg.mediaUrl && (
                          <div className="mb-1">
                            <img
                              src={msg.mediaUrl}
                              alt="attachment"
                              className="rounded-lg max-w-full mb-1 object-contain"
                              style={{ maxHeight: 200 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                            <a
                              href={msg.mediaUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              <Download className="w-3 h-3" />
                              {t('common.download')}
                            </a>
                          </div>
                        )}
                        {(msg.content || msg.message || msg.text) && (
                          <p className="leading-relaxed">{msg.content || msg.message || msg.text}</p>
                        )}
                        {msg.createdAt && (
                          <p className="text-xs mt-1 opacity-40">
                            {formatTime(msg.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedConversation(null)}
                className="btn btn-outline"
              >
                {t('chatbot.conv_close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversations;
